import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  createLessonPlan,
  updateLessonPlan,
  deleteLessonPlan,
  getLessonPlans
} from '../firebase/lessonService';
import './LessonPlanner.css';

function LessonPlanner() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonForm, setLessonForm] = useState({
    title: '',
    subject: '',
    grade: '',
    duration: '',
    date: '',
    objectives: '',
    materials: '',
    procedure: '',
    assessment: '',
    notes: ''
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState('all');

  useEffect(() => {
    if (currentUser) {
      loadLessons();
    }
  }, [currentUser]);

  const loadLessons = async () => {
    setLoading(true);
    const result = await getLessonPlans(currentUser.id);
    if (result.success) {
      setLessons(result.lessons);
    }
    setLoading(false);
  };

  const handleCreateLesson = () => {
    setEditingLesson(null);
    setLessonForm({
      title: '',
      subject: '',
      grade: '',
      duration: '',
      date: '',
      objectives: '',
      materials: '',
      procedure: '',
      assessment: '',
      notes: ''
    });
    setShowLessonModal(true);
  };

  const handleEditLesson = (lesson) => {
    setEditingLesson(lesson);
    setLessonForm({
      title: lesson.title || '',
      subject: lesson.subject || '',
      grade: lesson.grade || '',
      duration: lesson.duration || '',
      date: lesson.date || '',
      objectives: lesson.objectives || '',
      materials: lesson.materials || '',
      procedure: lesson.procedure || '',
      assessment: lesson.assessment || '',
      notes: lesson.notes || ''
    });
    setShowLessonModal(true);
  };

  const handleSaveLesson = async (e) => {
    e.preventDefault();
    if (!lessonForm.title || !lessonForm.subject) return;

    if (editingLesson) {
      const result = await updateLessonPlan(currentUser.id, editingLesson.id, lessonForm);
      if (result.success) {
        await loadLessons();
        closeModal();
      }
    } else {
      const result = await createLessonPlan(currentUser.id, lessonForm);
      if (result.success) {
        await loadLessons();
        closeModal();
      }
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (window.confirm('Are you sure you want to delete this lesson plan?')) {
      const result = await deleteLessonPlan(currentUser.id, lessonId);
      if (result.success) {
        await loadLessons();
        setSelectedLesson(null);
      }
    }
  };

  const closeModal = () => {
    setShowLessonModal(false);
    setEditingLesson(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const filteredLessons = lessons.filter(lesson => {
    const matchesSearch = lesson.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lesson.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterSubject === 'all' || lesson.subject === filterSubject;
    return matchesSearch && matchesFilter;
  });

  const subjects = [...new Set(lessons.map(lesson => lesson.subject))];

  if (loading) {
    return (
      <div className="lesson-planner-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading lesson plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lesson-planner-container">
      <header className="lesson-header">
        <div className="lesson-header-content">
          <div className="header-left">
            <button onClick={() => navigate('/')} className="back-button">
              ← Home
            </button>
            <h1>Lesson Planner</h1>
          </div>
          <div className="header-right">
            <button onClick={handleCreateLesson} className="create-button">
              + New Lesson Plan
            </button>
            <span className="user-email">{currentUser?.email}</span>
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </div>
        </div>
      </header>

      <div className="lesson-main">
        <div className="lesson-sidebar">
          <div className="search-section">
            <input
              type="text"
              placeholder="Search lessons..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-section">
            <label>Filter by Subject:</label>
            <select
              value={filterSubject}
              onChange={(e) => setFilterSubject(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Subjects</option>
              {subjects.map(subject => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>

          <div className="lessons-list">
            <h3>Your Lesson Plans ({filteredLessons.length})</h3>
            {filteredLessons.length === 0 ? (
              <p className="no-lessons">No lesson plans yet. Create your first one!</p>
            ) : (
              filteredLessons.map(lesson => (
                <div
                  key={lesson.id}
                  className={`lesson-card ${selectedLesson?.id === lesson.id ? 'selected' : ''}`}
                  onClick={() => setSelectedLesson(lesson)}
                >
                  <h4>{lesson.title}</h4>
                  <div className="lesson-meta">
                    <span className="subject-badge">{lesson.subject}</span>
                    {lesson.grade && <span className="grade-badge">Grade {lesson.grade}</span>}
                  </div>
                  {lesson.date && <p className="lesson-date">{lesson.date}</p>}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="lesson-content">
          {selectedLesson ? (
            <div className="lesson-detail">
              <div className="lesson-detail-header">
                <div>
                  <h2>{selectedLesson.title}</h2>
                  <div className="lesson-detail-meta">
                    <span className="subject-badge">{selectedLesson.subject}</span>
                    {selectedLesson.grade && <span className="grade-badge">Grade {selectedLesson.grade}</span>}
                    {selectedLesson.duration && <span className="duration-badge">{selectedLesson.duration} min</span>}
                  </div>
                  {selectedLesson.date && <p className="detail-date">Date: {selectedLesson.date}</p>}
                </div>
                <div className="lesson-actions">
                  <button onClick={() => handleEditLesson(selectedLesson)} className="edit-btn">
                    Edit
                  </button>
                  <button onClick={() => handleDeleteLesson(selectedLesson.id)} className="delete-btn">
                    Delete
                  </button>
                </div>
              </div>

              <div className="lesson-sections">
                {selectedLesson.objectives && (
                  <div className="lesson-section">
                    <h3>Learning Objectives</h3>
                    <p>{selectedLesson.objectives}</p>
                  </div>
                )}

                {selectedLesson.materials && (
                  <div className="lesson-section">
                    <h3>Materials Needed</h3>
                    <p>{selectedLesson.materials}</p>
                  </div>
                )}

                {selectedLesson.procedure && (
                  <div className="lesson-section">
                    <h3>Procedure</h3>
                    <p className="preserve-whitespace">{selectedLesson.procedure}</p>
                  </div>
                )}

                {selectedLesson.assessment && (
                  <div className="lesson-section">
                    <h3>Assessment</h3>
                    <p>{selectedLesson.assessment}</p>
                  </div>
                )}

                {selectedLesson.notes && (
                  <div className="lesson-section">
                    <h3>Notes</h3>
                    <p>{selectedLesson.notes}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <h2>No Lesson Selected</h2>
              <p>Select a lesson plan from the list or create a new one</p>
              <button onClick={handleCreateLesson} className="create-button-large">
                + Create Your First Lesson Plan
              </button>
            </div>
          )}
        </div>
      </div>

      {showLessonModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content lesson-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            <h2>{editingLesson ? 'Edit Lesson Plan' : 'New Lesson Plan'}</h2>

            <form onSubmit={handleSaveLesson} className="lesson-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="title">Lesson Title *</label>
                  <input
                    type="text"
                    id="title"
                    value={lessonForm.title}
                    onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                    placeholder="e.g., Introduction to Fractions"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <input
                    type="text"
                    id="subject"
                    value={lessonForm.subject}
                    onChange={(e) => setLessonForm({ ...lessonForm, subject: e.target.value })}
                    placeholder="e.g., Mathematics"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="grade">Grade Level</label>
                  <input
                    type="text"
                    id="grade"
                    value={lessonForm.grade}
                    onChange={(e) => setLessonForm({ ...lessonForm, grade: e.target.value })}
                    placeholder="e.g., 5"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="duration">Duration (minutes)</label>
                  <input
                    type="number"
                    id="duration"
                    value={lessonForm.duration}
                    onChange={(e) => setLessonForm({ ...lessonForm, duration: e.target.value })}
                    placeholder="e.g., 45"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="date">Date</label>
                  <input
                    type="date"
                    id="date"
                    value={lessonForm.date}
                    onChange={(e) => setLessonForm({ ...lessonForm, date: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="objectives">Learning Objectives</label>
                <textarea
                  id="objectives"
                  value={lessonForm.objectives}
                  onChange={(e) => setLessonForm({ ...lessonForm, objectives: e.target.value })}
                  placeholder="What will students learn?"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label htmlFor="materials">Materials Needed</label>
                <textarea
                  id="materials"
                  value={lessonForm.materials}
                  onChange={(e) => setLessonForm({ ...lessonForm, materials: e.target.value })}
                  placeholder="List materials and resources needed"
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label htmlFor="procedure">Procedure/Activities</label>
                <textarea
                  id="procedure"
                  value={lessonForm.procedure}
                  onChange={(e) => setLessonForm({ ...lessonForm, procedure: e.target.value })}
                  placeholder="Step-by-step lesson activities"
                  rows="6"
                />
              </div>

              <div className="form-group">
                <label htmlFor="assessment">Assessment</label>
                <textarea
                  id="assessment"
                  value={lessonForm.assessment}
                  onChange={(e) => setLessonForm({ ...lessonForm, assessment: e.target.value })}
                  placeholder="How will you assess student learning?"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label htmlFor="notes">Additional Notes</label>
                <textarea
                  id="notes"
                  value={lessonForm.notes}
                  onChange={(e) => setLessonForm({ ...lessonForm, notes: e.target.value })}
                  placeholder="Any additional notes or reflections"
                  rows="2"
                />
              </div>

              <div className="modal-buttons">
                <button type="submit" className="save-button">
                  {editingLesson ? 'Update' : 'Create'} Lesson Plan
                </button>
                <button type="button" onClick={closeModal} className="cancel-button">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default LessonPlanner;
